import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { getAuth } from 'firebase/auth';
import { toast } from 'react-toastify';

export default function BillboardMovieItem({ movie, rank }) {
    const auth = getAuth();
    const navigate = useNavigate();
    const [imageError, setImageError] = useState({
        poster: false,
        actor: false,
    });

    const handleMovieClick = () => {
        if (!auth.currentUser) {
            toast.error("Please sign in to view movie details");
            navigate('/welcome');
            return;
        }
        navigate(`/movie/${movie.id}`);
    };

    const handleImageError = (type) => {
        setImageError(prev => ({ ...prev, [type]: true }));
    };

    if (!movie.id) {
        throw new Error("BillboardMovieItem requires a movie object with an 'id' property");
    }

    return (
        <div
            className="bg-gray-800 rounded-xl p-4 mb-4 shadow-lg hover:shadow-xl transition-shadow duration-300 cursor-pointer"
            onClick={handleMovieClick}
        >
            {/* Desktop Layout - Row */}
            <div className="hidden md:flex items-center">
                {/* Rank Number */}
                <div className="flex-shrink-0 w-12 h-12 bg-pink-500 rounded-full flex items-center justify-center text-white font-bold text-lg mr-4">
                    {rank}
                </div>

                {/* Movie Cover Image */}
                <div className="flex-shrink-0 w-24 h-36 rounded-lg overflow-hidden mr-4">
                    {imageError.poster ? (
                        <div className="w-full h-full bg-gray-700 flex items-center justify-center">
                            <span className="text-gray-900"></span>
                        </div>
                    ) : (
                        <img
                            src={movie.posterUrl}
                            alt={movie.title}
                            className="w-full h-full object-cover"
                            loading="lazy"
                            decoding="async"
                            onError={() => handleImageError('poster')}
                        />
                    )}
                </div>

                {/* Movie Details */}
                <div className="flex-1 flex items-center space-x-4">
                    {/* Actor Profile */}
                    <div className="flex-shrink-0">
                        {movie.actorImageUrl ? (
                            imageError.actor ? (
                                <div className="w-12 h-12 rounded-full bg-gray-700 flex items-center justify-center">
                                    <span className="text-gray-900"></span>
                                </div>
                            ) : (
                                <img
                                    src={movie.actorImageUrl}
                                    alt={movie.mainActorName || "Main Actor"}
                                    className="w-12 h-12 rounded-full object-cover"
                                    onError={() => handleImageError('actor')}
                                />
                            )
                        ) : (
                            <div className="w-12 h-12 rounded-full bg-gray-700 flex items-center justify-center">
                                <span className="text-gray-900"></span>
                            </div>
                        )}
                    </div>

                    {/* Title, Actor Name, Year, and PG Rating */}
                    <div className="flex-1">
                        <h3 className="text-lg font-semibold text-white truncate">{movie.title}</h3>
                        <p className="text-sm text-gray-400">{movie.mainActorName || "Unknown Actor"}</p>
                        <div className="flex items-center space-x-2 mt-1">
                            <p className="text-sm text-gray-400">{movie.releaseYear}</p>
                            <span className="text-sm text-gray-400">•</span>
                            <p className="text-sm text-gray-400">{movie.contentRating || 'PG'}</p>
                        </div>
                    </div>
                </div>
            </div>

            {/* Mobile Layout - Column */}
            <div className="md:hidden flex flex-col">
                <div className="flex items-start mb-3">
                    {/* Rank Number */}
                    <div className="flex-shrink-0 w-12 h-12 bg-pink-500 rounded-full flex items-center justify-center text-white font-bold text-lg mr-4">
                        {rank}
                    </div>

                    {/* Title and Basic Info */}
                    <div className="flex-1">
                        <h3 className="text-lg font-semibold text-white truncate">{movie.title}</h3>
                        <div className="flex items-center space-x-2 mt-1">
                            <p className="text-sm text-gray-400">{movie.releaseYear}</p>
                            <span className="text-sm text-gray-400">•</span>
                            <p className="text-sm text-gray-400">{movie.contentRating || 'PG'}</p>
                        </div>
                    </div>
                </div>

                <div className="flex items-center space-x-4">
                    {/* Movie Cover Image */}
                    <div className="flex-shrink-0 w-20 h-28 rounded-lg overflow-hidden">
                        {imageError.poster ? (
                            <div className="w-full h-full bg-gray-700 flex items-center justify-center">
                                <span className="text-gray-900"></span>
                            </div>
                        ) : (
                            <img
                                src={movie.posterUrl}
                                alt={movie.title}
                                className="w-full h-full object-cover"
                                loading="lazy"
                                decoding="async"
                                onError={() => handleImageError('poster')}
                            />
                        )}
                    </div>

                    {/* Actor Info */}
                    <div className="flex-1 flex items-center space-x-3">
                        {/* Actor Profile */}
                        <div className="flex-shrink-0">
                            {movie.actorImageUrl ? (
                                imageError.actor ? (
                                    <div className="w-10 h-10 rounded-full bg-gray-700 flex items-center justify-center">
                                        <span className="text-gray-900"></span>
                                    </div>
                                ) : (
                                    <img
                                        src={movie.actorImageUrl}
                                        alt={movie.mainActorName || "Main Actor"}
                                        className="w-10 h-10 rounded-full object-cover"
                                        onError={() => handleImageError('actor')}
                                    />
                                )
                            ) : (
                                <div className="w-10 h-10 rounded-full bg-gray-700 flex items-center justify-center">
                                    <span className="text-gray-900"></span>
                                </div>
                            )}
                        </div>

                        {/* Actor Name */}
                        <div>
                            <p className="text-sm text-gray-400">{movie.mainActorName || "Unknown Actor"}</p>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}